import { getStoredUserInfo, setStoredProjectFile, setStoredUserInfo } from "../utils/storage";
import {
  Grid,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import React, { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Store } from "../Store.js";
import {
  convertToDate,
  getLanguageName,
  getTaskName,
} from "../utils/generalFunctions.js";
import GlobalButton from "../Components/Buttons/GlobalButton.js";
import classes from "../Assets/Styles/Global.module.css";
import { secondaryColor } from "../utils/constants.js";
import { getProjectsForSingleUser } from "../features/projects/api.js";

const LandingPage = () => {
  const navigate = useNavigate();
  const [myProjects, setMyProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo } = state;

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch data when user ID or debounced search term changes
  useEffect(() => {
    if (!userInfo?.id) return;

    (async () => {
      setIsSearching(true);
      try {
        const resp = await getProjectsForSingleUser({
          id: userInfo.id,
          searchTerm: debouncedSearchTerm
        });
        const arr = resp.data.projectsArray;
        setMyProjects(arr);

        const RolesByName = groupRolesByName(arr);
        ctxDispatch({ type: "EMPLOYEE_TASKS", payload: RolesByName });
        localStorage.setItem("employeeTasks", JSON.stringify(RolesByName));
      } catch (err) {
        console.log(err);
      } finally {
        setIsSearching(false);
      }
    })();
  }, [userInfo?.id, debouncedSearchTerm, ctxDispatch]);

  // Client-side filtering for immediate visual feedback while waiting for API
  const filteredProjects = useMemo(() => {
    if (!searchTerm.trim()) return myProjects;

    return myProjects.filter(project =>
      project.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [myProjects, searchTerm]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleWork = (project, role, language) => {
    ctxDispatch({ type: "PROJECT_FILE", payload: project });
    setStoredProjectFile(project);

    // Preserve user info in localStorage if not already there
    if (userInfo && !getStoredUserInfo()) {
      setStoredUserInfo(userInfo);
    }

    if (project.type === 'audiobook') {
      if (role === "performancedirection") {
        // Make sure userInfo is included in the navigation state
        navigate(`/audiobooks/performancedirection/${project.id}/${language}`, {
          state: { userInfo: userInfo }
        });
      }
      return;
    }

    if (role === "transcriber") navigate("/work/transcriber");
    else if (role === "translator") navigate(`/work/translator/${language}`);
    else if (role === "performancedirection")
      navigate(`/work/performancedirection/${language}`);
    else if (role === "lipqualityassurance")
      navigate(`/work/lipqualityassurance/${language}`);
    else if (role === "speakerfaceidentification")
      navigate("/work/speakerfaceidentification");
    else if (role === "qualityassurance")
      navigate(`/work/qualityassurance/${language}`);
  };

  const groupRolesByName = (data) => {
    const groupedData = data.reduce((acc, item) => {
      if (!acc[item.name]) {
        acc[item.name] = { name: item.name, roles: [] };
      }

      if (!acc[item.name].roles.includes(item.role)) {
        acc[item.name].roles.push(item.role);
      }

      return acc;
    }, {});

    return Object.values(groupedData);
  };

  useEffect(() => {
    ctxDispatch({ type: "PROJECT_FILE_CLEAR" });
    // localStorage.removeItem("projectFile");
  }, [ctxDispatch]);

  return (
    <Grid item container justifyContent="center" marginBottom="29.3%">
      <Grid item container xs={8.5} marginTop={7} direction="row">
        <Grid
          item
          marginBottom={3}
          xs={12}
          sx={{
            fontSize: "var(--font-size-header)",
            fontWeight: "900",
          }}
          className={classes["header"]}
        >
          View my Assigned Projects
        </Grid>

        <Grid item container xs={12} justifyContent="flex-end" marginBottom={2}>
          <GlobalButton
            color="primary"
            onClick={() => {
              navigate("/file-upload");
            }}
          >
            Upload File
          </GlobalButton>
        </Grid>

        {/* Search Bar */}
        <Grid item container xs={12} marginBottom={2}>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: isSearching && (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
            />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Table sx={{ minWidth: 650, bgcolor: "white" }}>
            <TableHead bgcolor="#5570dc">
              <TableRow>
                <TableCell>Project Name</TableCell>
                <TableCell>My Role</TableCell>
                <TableCell>View Original Content</TableCell>
                <TableCell>Original Language</TableCell>
                <TableCell>Final Languages</TableCell>
                <TableCell>Creation Date</TableCell>
                <TableCell>ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProjects.length > 0 ? (
                filteredProjects.map((project, index) => (
                  <TableRow key={index}>
                    <TableCell style={{ fontWeight: "600" }}>
                      {project.name}
                    </TableCell>
                    <TableCell>{getTaskName(project.role)}</TableCell>
                    <TableCell>
                      <a
                        style={{
                          textDecoration: "none",
                          color: `${secondaryColor}`,
                        }}
                        href={project.videoUrl}
                      >
                        Content
                      </a>
                    </TableCell>
                    <TableCell>{getLanguageName(project.orginalLang)}</TableCell>
                    <TableCell width="30%">
                      <Grid
                        item
                        container
                        direction="row"
                        gap={1}
                        alignItems="center"
                        justifyContent="center"
                      >
                        {project.finalLang?.map((language, index) => (
                          <Grid
                            xs={5.5}
                            item
                            container
                            key={index}
                            alignItems="center"
                            justifyContent="center"
                            width="300px"
                            height="32px"
                            sx={{
                              background: "lightgrey",
                              fontWeight: "400",
                              borderRadius: "var(--radius-md)",
                            }}
                          >
                            <Grid item>{getLanguageName(language)}</Grid>
                          </Grid>
                        ))}
                      </Grid>
                    </TableCell>
                    <TableCell>{convertToDate(project.creationDate)}</TableCell>
                    <TableCell>
                      {project.role === "translator" ||
                        project.role === "qualityassurance" ||
                        project.role === "performancedirection" ||
                        project.role === "lipqualityassurance" ? (
                        <Select
                          color="primary"
                          defaultValue="Start Working"
                          size="small"
                        >
                          <MenuItem value="Start Working" disabled>
                            Start Working
                          </MenuItem>
                          {project.finalLang.map((language, index) => (
                            <MenuItem
                              key={index}
                              value={language}
                              onClick={() =>
                                handleWork(project, project.role, language)
                              }
                            >
                              {getLanguageName(language)}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <GlobalButton
                          color="primary"
                          onClick={() => {
                            handleWork(project, project.role);
                          }}
                        >
                          Start Working
                        </GlobalButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No projects found matching your search
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default LandingPage;
